import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig, validateMsalConfig } from './config/authConfig';
import { initializeMsal } from './services/authService';

// Validate MSAL configuration
try {
  validateMsalConfig();
} catch (error) {
  console.warn('MSAL configuration validation failed:', error);
  // Continue anyway - will use placeholder values
}

// Create MSAL instance (singleton)
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL asynchronously before any operations
(async () => {
  try {
    // CRITICAL: Must call initialize() before any MSAL operations (MSAL Browser 4.x requirement)
    await msalInstance.initialize();
    console.log('MSAL instance initialized successfully');

    // Initialize MSAL in authService
    initializeMsal(msalInstance);

    // Handle redirect promise after initialization
    const response = await msalInstance.handleRedirectPromise();

    if (response) {
      console.log('Redirect response received:', response);
      // Set active account after successful login
      msalInstance.setActiveAccount(response.account);

      // Pre-acquire token to ensure it's cached for subsequent API calls
      try {
        const { waitForToken } = await import('./services/authService');
        const token = await waitForToken(5000);
        if (token) {
          console.log('Token pre-acquired successfully after login');
        } else {
          console.warn('Failed to pre-acquire token after login');
        }
      } catch (tokenError) {
        console.error('Error pre-acquiring token:', tokenError);
      }
    } else {
      // Check if there are any accounts already signed in
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);

        // Pre-acquire token for existing sessions as well
        try {
          const { waitForToken } = await import('./services/authService');
          const token = await waitForToken(3000);
          if (token) {
            console.log('Token pre-acquired successfully for existing session');
          }
        } catch (tokenError) {
          console.warn('Could not pre-acquire token for existing session:', tokenError);
        }
      }
    }
  } catch (error) {
    console.error('Error during MSAL initialization or redirect handling:', error);
  }
})();

const renderApp = () => {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </React.StrictMode>
  );

  // Hide loading indicator once React app is rendered
  setTimeout(() => {
    const loadingIndicator = document.getElementById('loading-indicator');
    if (loadingIndicator) {
      loadingIndicator.style.display = 'none';
    }
  }, 100);
};

// Enhanced Office.js initialization for PowerPoint add-in
const initializeApp = () => {
  // Check if we're in Office environment
  if (typeof Office !== 'undefined') {
    console.log('🏢 Office.js detected - initializing PowerPoint add-in...');

    Office.onReady((info) => {
      console.log('✅ Office.js ready!', info);

      if (info.host === Office.HostType.PowerPoint) {
        console.log('📊 PowerPoint host confirmed');
      } else {
        console.log('⚠️ Not running in PowerPoint, host:', info.host);
      }

      renderApp();
    });
  } else {
    // Browser environment - render immediately for development
    console.log('🌐 Browser environment - rendering directly');
    renderApp();
  }
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}
